import { useSessionRecovery } from '@/stores/sessionRecovery';
import { sessionBlocked, useSessionTransition } from '@/stores/sessionTransition';
import { io, Socket } from 'socket.io-client';

let socket: Socket | null = null;

// Only log in development mode
const isDev = import.meta.env.DEV;
const log = (message: string, ...args: unknown[]) => {
  if (isDev) console.log(message, ...args);
};
const logError = (message: string, ...args: unknown[]) => {
  if (isDev) console.error(message, ...args);
};

export interface SocketConfig {
  url?: string;
  token: string;
}

export function getSocket(config?: SocketConfig): Socket | null {
  if (sessionBlocked() || !config?.token) {
    return null;
  }

  if (!socket) {
    const url = config?.url || import.meta.env.VITE_API_URL || 'http://localhost:3000';

    socket = io(`${url}/realtime`, {
      auth: {
        token: config.token,
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    socket.on('connect', () => {
      log('[Socket] Connected to realtime server');
    });

    socket.on('disconnect', (reason) => {
      log('[Socket] Disconnected:', reason);
    });

    socket.on('connect_error', (error) => {
      logError('[Socket] Connection error:', error.message);
    });

    socket.on('connected', (data) => {
      log('[Socket] Joined room:', data.room);
    });
  }

  return sessionBlocked() ? null : socket;
}

export function disconnectSocket() {
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
    log('[Socket] Manually disconnected');
  }
}

export function isSocketConnected(): boolean {
  return socket?.connected ?? false;
}

export function getExistingSocket(): Socket | null {
  return sessionBlocked() ? null : socket;
}

useSessionRecovery.subscribe((state) => {
  if (state.phase !== 'idle') disconnectSocket();
});

useSessionTransition.subscribe((state) => {
  if (state.isSwitchingTenant) disconnectSocket();
});
